import { useState, useMemo, useRef } from 'react'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts'
import { useFinance } from '../../context/FinanceContext'
import { getThemeColors } from '../../lib/constants'
import { formatCurrency, formatCurrencyExact } from '../../lib/formatters'
import { calculateDebtVsInvest, calculateMonthlyPayment } from '../../lib/calculations'
import GlassCard from '../ui/GlassCard'
import Button from '../ui/Button'
import { addChartToPDF } from '../../lib/pdfChartCapture'
import { useSubscription } from '../../context/SubscriptionContext'
import { trackExportReport } from '../../lib/analytics'

const PATH_A_COLOR = '#a855f7' // purple — pay off debt then invest
const PATH_B_COLOR = '#22d3ee' // cyan — invest from day one

const LOAN_TYPE_PRESETS = {
  custom:   { label: 'Custom',        icon: '📋', rate: 6,    term: 120 },
  student:  { label: 'Student Loan',  icon: '🎓', rate: 5.5,  term: 120 },
  auto:     { label: 'Auto Loan',     icon: '🚗', rate: 5.9,  term: 60 },
  personal: { label: 'Personal Loan', icon: '💰', rate: 10.5, term: 48 },
  credit:   { label: 'Credit Card',   icon: '💳', rate: 24.99, term: 60 },
  mortgage: { label: 'Mortgage',      icon: '🏠', rate: 6.5,  term: 360 },
}

const STUDENT_REPAYMENT_PLANS = [
  { key: 'standard',     label: 'Standard',        term: 120,  note: '10 years, fixed payments' },
  { key: 'graduated',    label: 'Graduated',        term: 120,  note: '10 years, payments increase every 2 years' },
  { key: 'extended',     label: 'Extended Fixed',    term: 300,  note: '25 years, fixed payments (must owe 30k+)' },
  { key: 'ext_grad',     label: 'Extended Graduated',term: 300,  note: '25 years, payments increase (must owe 30k+)' },
  { key: 'save',         label: 'SAVE / REPAYE',     term: 240,  note: '20-25 years, income-driven' },
  { key: 'ibr',          label: 'IBR',               term: 300,  note: '20-25 years, income-driven' },
  { key: 'paye',         label: 'PAYE',              term: 240,  note: '20 years, income-driven' },
  { key: 'icr',          label: 'ICR',               term: 300,  note: '25 years, income-driven' },
  { key: 'custom',       label: 'Custom Term',       term: null, note: 'Set your own term' },
]

export default function DebtVsInvest() {
  const { state } = useFinance()
  const { isPro } = useSubscription()
  const colors = getThemeColors(state.theme)
  const chartRef = useRef(null)
  const balanceChartRef = useRef(null)

  const [loanType, setLoanType] = useState('student')
  const [repaymentPlan, setRepaymentPlan] = useState('standard')
  const [loanBalance, setLoanBalance] = useState(10000)
  const [loanRate, setLoanRate] = useState(5.5)
  const [termMonths, setTermMonths] = useState(120)
  const [extraMonthly, setExtraMonthly] = useState(500)
  const [investReturnRate, setInvestReturnRate] = useState(8)

  const handleLoanTypeChange = (type) => {
    const preset = LOAN_TYPE_PRESETS[type]
    setLoanType(type)
    setLoanRate(preset.rate)
    setTermMonths(preset.term)
    if (type === 'student') {
      setRepaymentPlan('standard')
    }
  }

  const handleRepaymentPlanChange = (planKey) => {
    setRepaymentPlan(planKey)
    const plan = STUDENT_REPAYMENT_PLANS.find(p => p.key === planKey)
    if (plan && plan.term !== null) {
      setTermMonths(plan.term)
    }
  }

  const minimumPayment = useMemo(
    () => Math.round(calculateMonthlyPayment(loanBalance, loanRate, termMonths) * 100) / 100,
    [loanBalance, loanRate, termMonths]
  )

  const result = useMemo(() => {
    if (loanBalance <= 0 || minimumPayment <= 0 || extraMonthly <= 0) return null
    return calculateDebtVsInvest({
      loanBalance,
      loanRate,
      minimumPayment,
      extraMonthly,
      investReturnRate,
    })
  }, [loanBalance, loanRate, minimumPayment, extraMonthly, investReturnRate])

  // Net worth chart
  const netWorthChartData = useMemo(() => {
    if (!result) return []
    const { schedule } = result
    const step = Math.max(1, Math.floor(schedule.length / 60))
    return schedule
      .filter((_, i) => i % step === 0 || i === schedule.length - 1)
      .map(row => ({
        month: row.month,
        label: row.month % 12 === 0 ? `Yr ${row.month / 12}` : `Mo ${row.month}`,
        'Path A: Pay Off + Invest': row.a_netWorth,
        'Path B: Invest from Day 1': row.b_netWorth,
      }))
  }, [result])

  // Balance breakdown chart
  const balanceChartData = useMemo(() => {
    if (!result) return []
    const { schedule } = result
    const step = Math.max(1, Math.floor(schedule.length / 60))
    return schedule
      .filter((_, i) => i % step === 0 || i === schedule.length - 1)
      .map(row => ({
        month: row.month,
        label: row.month % 12 === 0 ? `Yr ${row.month / 12}` : `Mo ${row.month}`,
        'A: Investment': row.a_investBalance,
        'A: Loan': row.a_loanBalance,
        'B: Investment': row.b_investBalance,
        'B: Loan': row.b_loanBalance,
      }))
  }, [result])

  const handleExportPDF = async () => {
    if (!result) return
    trackExportReport()
    const { default: jsPDF } = await import('jspdf')
    const { default: autoTable } = await import('jspdf-autotable')

    const doc = new jsPDF()
    const m = 14
    let y = m

    const heading = (text, size = 16) => {
      doc.setFontSize(size)
      doc.setTextColor(0)
      doc.text(text, m, y)
      y += size === 16 ? 8 : 6
    }
    const subtext = (text) => {
      doc.setFontSize(9)
      doc.setTextColor(100)
      const lines = doc.splitTextToSize(text, doc.internal.pageSize.getWidth() - m * 2)
      doc.text(lines, m, y)
      y += lines.length * 4 + 2
    }
    const checkPage = (need = 30) => {
      if (y + need > doc.internal.pageSize.getHeight() - m) {
        doc.addPage()
        y = m
      }
    }

    // ===== Page 1: Title + Inputs =====
    doc.setFontSize(22)
    doc.setTextColor(0)
    doc.text('Debt vs. Invest Comparison', m, y + 10)
    y += 14
    doc.setFontSize(10)
    doc.setTextColor(128)
    const loanTypeLabel = LOAN_TYPE_PRESETS[loanType]?.label || 'Custom'
    const planLabel = loanType === 'student' ? ` - ${STUDENT_REPAYMENT_PLANS.find(p => p.key === repaymentPlan)?.label || ''}` : ''
    doc.text(`Generated by Liquid Finance  |  ${new Date().toLocaleDateString()}  |  ${loanTypeLabel}${planLabel}`, m, y)
    y += 6
    doc.setTextColor(100)
    doc.setFontSize(9)
    doc.text('Should you pay off debt faster or invest the extra?', m, y)
    y += 8

    // Inputs
    heading('Loan Details')
    const inputRows = [
      ['Loan Type', `${loanTypeLabel}${planLabel}`],
      ['Loan Balance', formatCurrency(loanBalance)],
      ['Interest Rate', `${loanRate}%`],
      ['Loan Term', `${termMonths} months (${formatMonths(termMonths)})`],
      ['Minimum Payment', formatCurrencyExact(minimumPayment) + '/mo (calculated from loan terms)'],
      ['Extra Monthly Amount', formatCurrencyExact(extraMonthly) + '/mo'],
      ['Expected Investment Return', `${investReturnRate}%`],
    ]
    if (loanType === 'student' && repaymentPlan) {
      const planInfo = STUDENT_REPAYMENT_PLANS.find(p => p.key === repaymentPlan)
      if (planInfo) {
        inputRows.push(['Repayment Plan Note', planInfo.note])
      }
    }
    autoTable(doc, {
      body: inputRows,
      startY: y,
      theme: 'grid',
      styles: { fontSize: 9 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55 } },
    })
    y = doc.lastAutoTable.finalY + 8

    // ===== Winner Banner =====
    checkPage(30)
    heading('Winner')
    const winnerPath = result.winner === 'payoff' ? 'Path A' : 'Path B'
    const winnerDesc = result.winner === 'payoff'
      ? 'Pay off debt first, then invest!'
      : 'Invest from day one!'
    const winnerDetail = result.winner === 'payoff'
      ? `After ${formatMonths(result.fullTermMonths)} (full loan term), paying off the loan early then investing leaves you ahead -- ${formatCurrency(result.a_netWorth)} net vs ${formatCurrency(result.b_adjustedNetWorth)} adjusted`
      : `After ${formatMonths(result.fullTermMonths)} (full loan term), investing from day one wins even after subtracting the ${formatCurrency(result.extraInterestCost)} extra interest cost`

    autoTable(doc, {
      body: [
        ['Winner', `${winnerPath}: ${winnerDesc}`],
        ['Net Advantage', `+${formatCurrency(result.advantage)}`],
        ['Explanation', winnerDetail],
      ],
      startY: y,
      theme: 'grid',
      styles: { fontSize: 9 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 35 } },
    })
    y = doc.lastAutoTable.finalY + 8

    // ===== Loan Baseline =====
    checkPage(30)
    heading('Loan Baseline (minimum payments only)')
    autoTable(doc, {
      body: [
        ['Loan Amount', formatCurrency(loanBalance)],
        ['Full Payoff Time', formatMonths(result.baselinePayoffMonth)],
        ['Total Interest (full term)', formatCurrency(result.baselineTotalInterest)],
        ['Min Payment', formatCurrencyExact(minimumPayment) + '/mo'],
      ],
      startY: y,
      theme: 'grid',
      styles: { fontSize: 9 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55 } },
    })
    y = doc.lastAutoTable.finalY + 8

    // ===== Path A Detail =====
    checkPage(70)
    heading('Path A: Pay Off Debt, Then Invest')
    subtext(`Pay ${formatCurrencyExact(minimumPayment + extraMonthly)}/mo to kill the loan fast, then invest the full amount for the rest of the term`)

    autoTable(doc, {
      head: [['', 'Loan Phase', 'Investment Phase', 'Bottom Line']],
      body: [
        ['Monthly Payment', formatCurrencyExact(minimumPayment + extraMonthly), formatCurrencyExact(minimumPayment + extraMonthly) + ' (invested)', ''],
        ['Duration', formatMonths(result.a_payoffMonth), formatMonths(result.fullTermMonths - result.a_payoffMonth), formatMonths(result.fullTermMonths) + ' total'],
        ['Time Saved vs Full Term', formatMonths(result.timeSaved), '', ''],
        ['Interest Paid', formatCurrency(result.a_totalInterest), '', ''],
        ['Interest Saved vs Full Term', formatCurrency(result.interestSaved), '', ''],
        ['Total Contributed', '', formatCurrency(result.a_totalContributed), ''],
        ['Investment Growth', '', formatCurrency(result.a_totalInvestGrowth), ''],
        ['Investment Balance', '', formatCurrency(result.a_finalInvestment), ''],
        ['Net Worth', '', '', formatCurrency(result.a_netWorth)],
        ['Breakdown', '', '', `$0 debt + ${formatCurrency(result.a_finalInvestment)} invested`],
      ],
      startY: y,
      theme: 'grid',
      styles: { fontSize: 8 },
      headStyles: { fillColor: [168, 85, 247] },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 42 } },
    })
    y = doc.lastAutoTable.finalY + 8

    // ===== Path B Detail =====
    checkPage(70)
    heading('Path B: Invest from Day One')
    subtext(`Pay ${formatCurrencyExact(minimumPayment)}/mo minimum on loan, invest ${formatCurrencyExact(extraMonthly)}/mo at ${investReturnRate}% from month 1`)

    autoTable(doc, {
      head: [['', 'Loan (min payments)', 'Investment (entire term)', 'Bottom Line']],
      body: [
        ['Monthly Payment', formatCurrencyExact(minimumPayment), formatCurrencyExact(extraMonthly) + ' (invested)', ''],
        ['Duration', formatMonths(result.baselinePayoffMonth), formatMonths(result.fullTermMonths), ''],
        ['Total Interest (full term)', formatCurrency(result.b_totalInterest), '', ''],
        ['Extra Interest vs Path A', '-' + formatCurrency(result.extraInterestCost), '', ''],
        ['Total Contributed', '', formatCurrency(result.b_totalContributed), ''],
        ['Investment Growth', '', formatCurrency(result.b_totalInvestGrowth), ''],
        ['Investment Balance', '', formatCurrency(result.b_finalInvestment), ''],
        ...(result.b_remainingLoan > 0 ? [['Remaining Loan', '', '', '-' + formatCurrency(result.b_remainingLoan)]] : []),
        ['Extra Interest Cost vs Path A', '', '', '-' + formatCurrency(result.extraInterestCost)],
        ['Adjusted Net Worth', '', '', formatCurrency(result.b_adjustedNetWorth)],
        ['Breakdown', '', '', `${formatCurrency(result.b_finalInvestment)} invested${result.b_remainingLoan > 0 ? ` - ${formatCurrency(result.b_remainingLoan)} loan` : ''} - ${formatCurrency(result.extraInterestCost)} extra interest`],
      ],
      startY: y,
      theme: 'grid',
      styles: { fontSize: 8 },
      headStyles: { fillColor: [34, 211, 238] },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 42 } },
    })
    y = doc.lastAutoTable.finalY + 8

    // ===== Money Breakdown =====
    checkPage(60)
    heading('The Money Breakdown')

    autoTable(doc, {
      head: [['Metric', 'Path A', 'Path B']],
      body: [
        ['Interest Saved vs Baseline', formatCurrency(result.interestSaved), '-'],
        ['Interest Saved Note', `${formatCurrency(result.baselineTotalInterest)} baseline - ${formatCurrency(result.a_totalInterest)} accelerated`, `Pays ${formatCurrency(result.b_totalInterest)} over full ${formatMonths(result.fullTermMonths)}`],
        ['Investment Balance', formatCurrency(result.a_finalInvestment), formatCurrency(result.b_finalInvestment)],
        ['Investment Note', `${formatCurrency(result.a_totalContributed)} over ${formatMonths(result.fullTermMonths - result.a_payoffMonth)} + ${formatCurrency(result.a_totalInvestGrowth)} growth`, `${formatCurrency(result.b_totalContributed)} over ${formatMonths(result.fullTermMonths)} + ${formatCurrency(result.b_totalInvestGrowth)} growth`],
        ['Extra Interest Cost', '-', formatCurrency(result.extraInterestCost)],
        ['Extra Interest Note', '-', `${formatCurrency(result.b_totalInterest)} total vs ${formatCurrency(result.a_totalInterest)} = ${formatCurrency(result.extraInterestCost)} more`],
        ['', '', ''],
        ['Net Worth', formatCurrency(result.a_netWorth), formatCurrency(result.b_adjustedNetWorth) + ' (adjusted)'],
        ['Winner', '', `${winnerPath} by +${formatCurrency(result.advantage)}`],
      ],
      startY: y,
      theme: 'grid',
      styles: { fontSize: 8 },
      headStyles: { fillColor: [80, 80, 80] },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 42 } },
    })
    y = doc.lastAutoTable.finalY + 8

    // ===== How It Works =====
    checkPage(50)
    heading('How It Works')

    subtext(`We compare both paths over the full ${formatMonths(result.fullTermMonths)} loan term. Without extra payments, this loan costs ${formatCurrency(result.baselineTotalInterest)} in interest.`)

    subtext(`Path A (Pay Off + Invest): You pay ${formatCurrencyExact(minimumPayment + extraMonthly)}/mo and the loan is gone in ${formatMonths(result.a_payoffMonth)}, saving ${formatCurrency(result.interestSaved)} in interest. Then you invest the full ${formatCurrencyExact(minimumPayment + extraMonthly)}/mo at ${investReturnRate}% for the remaining ${formatMonths(result.fullTermMonths - result.a_payoffMonth)}. You contribute ${formatCurrency(result.a_totalContributed)} which grows to ${formatCurrency(result.a_finalInvestment)}.`)

    subtext(`Path B (Invest from Day 1): You make ${formatCurrencyExact(minimumPayment)}/mo minimum payments and invest ${formatCurrencyExact(extraMonthly)}/mo at ${investReturnRate}% for the full ${formatMonths(result.fullTermMonths)}. Your investments grow to ${formatCurrency(result.b_finalInvestment)}. But you pay ${formatCurrency(result.b_totalInterest)} in total loan interest -- that's ${formatCurrency(result.extraInterestCost)} more than Path A. Adjusted net worth: ${formatCurrency(result.b_finalInvestment)} - ${formatCurrency(result.extraInterestCost)} = ${formatCurrency(result.b_adjustedNetWorth)}.`)

    subtext(`Key tradeoff: Path A invests more per month (${formatCurrencyExact(minimumPayment + extraMonthly)} vs ${formatCurrencyExact(extraMonthly)}) but starts later. Path B invests less per month but compounds for longer -- however, the extra interest cost of not paying off the loan eats into those gains. This assumes consistent ${investReturnRate}% returns -- real markets fluctuate.`)

    // ===== Charts =====
    if (netWorthChartData.length > 1) {
      await addChartToPDF(doc, chartRef, { title: 'Net Worth Over Time' })
    }
    if (balanceChartData.length > 1) {
      await addChartToPDF(doc, balanceChartRef, { title: 'Balance Breakdown' })
    }

    // ===== Month-by-month schedule =====
    doc.addPage()
    y = m
    heading('Month-by-Month Schedule')
    subtext('Sampled every 3 months for readability. Full schedule available in the app.')

    const scheduleRows = result.schedule
      .filter((_, i) => i % 3 === 0 || i === result.schedule.length - 1)
      .map(row => [
        row.month,
        formatCurrency(row.a_loanBalance),
        formatCurrency(row.a_investBalance),
        formatCurrency(row.a_netWorth),
        formatCurrency(row.b_loanBalance),
        formatCurrency(row.b_investBalance),
        formatCurrency(row.b_netWorth),
      ])

    autoTable(doc, {
      head: [['Mo', 'A: Loan', 'A: Invest', 'A: Net Worth', 'B: Loan', 'B: Invest', 'B: Net Worth']],
      body: scheduleRows,
      startY: y,
      theme: 'striped',
      styles: { fontSize: 7 },
      headStyles: { fillColor: [80, 80, 80] },
    })

    doc.save(`debt-vs-invest-${Date.now()}.pdf`)
  }

  return (
    <div className="space-y-6">
      {/* Inputs */}
      <GlassCard>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <h3 className="text-sm font-semibold text-body">Debt vs. Invest</h3>
            <p className="text-xs text-muted mt-0.5">Should you pay off debt faster or invest the extra?</p>
          </div>
          <Button variant="secondary" size="sm" onClick={handleExportPDF} disabled={!isPro || !result} title={!isPro ? 'Pro feature' : ''}>
            Export PDF{!isPro ? ' (Pro)' : ''}
          </Button>
        </div>

        {/* Loan Type Presets */}
        <div className="mb-4">
          <p className="text-xs font-medium text-muted mb-2">Loan Type</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(LOAN_TYPE_PRESETS).map(([key, p]) => (
              <button
                key={key}
                onClick={() => handleLoanTypeChange(key)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                  loanType === key
                    ? 'text-heading shadow-sm'
                    : 'text-muted hover:text-heading bg-hover'
                }`}
                style={loanType === key ? {
                  background: `linear-gradient(135deg, ${colors.primary}22, ${colors.secondary}22)`,
                  border: `1px solid ${colors.primary}44`,
                } : {}}
              >
                <span>{p.icon}</span>
                <span className="hidden sm:inline">{p.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Student Loan Repayment Plan */}
        {loanType === 'student' && (
          <div className="mb-4">
            <p className="text-xs font-medium text-muted mb-2">Repayment Plan</p>
            <div className="flex flex-wrap gap-2">
              {STUDENT_REPAYMENT_PLANS.map((plan) => (
                <button
                  key={plan.key}
                  onClick={() => handleRepaymentPlanChange(plan.key)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    repaymentPlan === plan.key
                      ? 'text-heading shadow-sm'
                      : 'text-muted hover:text-heading bg-hover'
                  }`}
                  style={repaymentPlan === plan.key ? {
                    background: `linear-gradient(135deg, ${colors.primary}22, ${colors.secondary}22)`,
                    border: `1px solid ${colors.primary}44`,
                  } : {}}
                  title={plan.note}
                >
                  {plan.label}
                </button>
              ))}
            </div>
            {repaymentPlan && (
              <p className="text-xs text-faint mt-1.5">
                {STUDENT_REPAYMENT_PLANS.find(p => p.key === repaymentPlan)?.note}
              </p>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <InputField label="Loan Balance" value={loanBalance} onChange={setLoanBalance} prefix="$" min={0} />
          <InputField label="Interest Rate" value={loanRate} onChange={setLoanRate} suffix="%" step={0.1} min={0} />
          <InputField label="Term" value={termMonths} onChange={setTermMonths} suffix="months" min={1} />
          <div>
            <label className="block text-xs font-medium text-muted mb-1.5">Minimum Payment</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-faint">$</span>
              <input
                type="text"
                value={formatCurrencyExact(minimumPayment).replace('$', '')}
                disabled
                className="w-full bg-input border border-input rounded-xl py-2.5 text-sm text-muted focus:outline-none pl-7 pr-3 opacity-60 cursor-not-allowed"
              />
            </div>
            <p className="text-xs text-faint mt-0.5">Calculated from loan terms</p>
          </div>
          <InputField label="Extra Monthly Amount" value={extraMonthly} onChange={setExtraMonthly} prefix="$" min={0} />
          <InputField label="Expected Investment Return" value={investReturnRate} onChange={setInvestReturnRate} suffix="%" step={0.5} min={0} />
        </div>
      </GlassCard>

      {result && (
        <>
          {/* Winner Banner */}
          <GlassCard>
            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center text-xl shrink-0"
                style={{
                  background: `linear-gradient(135deg, ${result.winner === 'payoff' ? PATH_A_COLOR : PATH_B_COLOR}33, ${result.winner === 'payoff' ? PATH_A_COLOR : PATH_B_COLOR}11)`,
                }}
              >
                {result.winner === 'payoff' ? '🏦' : '📈'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-heading">
                  {result.winner === 'payoff'
                    ? 'Pay off debt first, then invest!'
                    : 'Invest from day one!'}
                </p>
                <p className="text-xs text-muted mt-0.5">
                  After {formatMonths(result.fullTermMonths)} (full loan term),{' '}
                  {result.winner === 'payoff'
                    ? `paying off the loan early then investing leaves you ahead — ${formatCurrency(result.a_netWorth)} net vs ${formatCurrency(result.b_adjustedNetWorth)} adjusted`
                    : `investing from day one wins even after subtracting the ${formatCurrency(result.extraInterestCost)} extra interest cost`}
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-2xl font-bold" style={{ color: result.winner === 'payoff' ? PATH_A_COLOR : PATH_B_COLOR }}>
                  +{formatCurrency(result.advantage)}
                </p>
                <p className="text-xs text-muted">net advantage</p>
              </div>
            </div>
          </GlassCard>

          {/* Loan Baseline */}
          <GlassCard>
            <h3 className="text-sm font-semibold text-body mb-3">Loan Baseline (minimum payments only)</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <MiniStat label="Loan Amount" value={formatCurrency(loanBalance)} />
              <MiniStat label="Full Payoff Time" value={formatMonths(result.baselinePayoffMonth)} />
              <MiniStat label="Total Interest (full term)" value={formatCurrency(result.baselineTotalInterest)} />
              <MiniStat label="Min Payment" value={formatCurrencyExact(minimumPayment) + '/mo'} />
            </div>
          </GlassCard>

          {/* Side-by-side results */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Path A */}
            <GlassCard>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PATH_A_COLOR }} />
                <h3 className="text-sm font-semibold text-body">Path A: Pay Off Debt, Then Invest</h3>
                {result.winner === 'payoff' && (
                  <span className="ml-auto text-xs font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: PATH_A_COLOR + '22', color: PATH_A_COLOR }}>
                    Winner
                  </span>
                )}
              </div>
              <p className="text-xs text-muted mb-3">
                Pay {formatCurrencyExact(minimumPayment + extraMonthly)}/mo to kill the loan fast, then invest the full amount for the rest of the term
              </p>

              {/* Loan phase */}
              <p className="text-xs font-semibold text-muted uppercase tracking-wide mb-2">Loan Phase</p>
              <div className="space-y-2 mb-4">
                <ResultRow label="Monthly Payment" value={formatCurrencyExact(minimumPayment + extraMonthly)} />
                <ResultRow label="Loan Paid Off In" value={formatMonths(result.a_payoffMonth)} />
                <ResultRow label="Time Saved vs Full Term" value={formatMonths(result.timeSaved)} positive />
                <ResultRow label="Interest Paid" value={formatCurrency(result.a_totalInterest)} />
                <ResultRow label="Interest Saved vs Full Term" value={formatCurrency(result.interestSaved)} positive />
              </div>

              {/* Investment phase */}
              <p className="text-xs font-semibold text-muted uppercase tracking-wide mb-2">Investment Phase (after payoff)</p>
              <div className="space-y-2 mb-4">
                <ResultRow label="Investing For" value={formatMonths(result.fullTermMonths - result.a_payoffMonth)} />
                <ResultRow label="Monthly Investment" value={formatCurrencyExact(minimumPayment + extraMonthly)} />
                <ResultRow label="Total Contributed" value={formatCurrency(result.a_totalContributed)} />
                <ResultRow label="Investment Growth" value={formatCurrency(result.a_totalInvestGrowth)} positive />
                <ResultRow label="Investment Balance" value={formatCurrency(result.a_finalInvestment)} />
              </div>

              <div className="border-t border-subtle pt-3">
                <ResultRow label={`Net Worth at ${formatMonths(result.fullTermMonths)}`} value={formatCurrency(result.a_netWorth)} highlight color={PATH_A_COLOR} />
                <p className="text-xs text-faint mt-1">Debt: $0 + Investments: {formatCurrency(result.a_finalInvestment)}</p>
              </div>
            </GlassCard>

            {/* Path B */}
            <GlassCard>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PATH_B_COLOR }} />
                <h3 className="text-sm font-semibold text-body">Path B: Invest from Day One</h3>
                {result.winner === 'invest' && (
                  <span className="ml-auto text-xs font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: PATH_B_COLOR + '22', color: PATH_B_COLOR }}>
                    Winner
                  </span>
                )}
              </div>
              <p className="text-xs text-muted mb-3">
                Pay {formatCurrencyExact(minimumPayment)}/mo minimum on loan, invest {formatCurrencyExact(extraMonthly)}/mo at {investReturnRate}% from month 1
              </p>

              {/* Loan */}
              <p className="text-xs font-semibold text-muted uppercase tracking-wide mb-2">Loan (minimum payments)</p>
              <div className="space-y-2 mb-4">
                <ResultRow label="Monthly Payment" value={formatCurrencyExact(minimumPayment)} />
                <ResultRow label="Loan Paid Off In" value={formatMonths(result.baselinePayoffMonth)} />
                <ResultRow label="Total Interest (full term)" value={formatCurrency(result.b_totalInterest)} />
                <ResultRow label="Extra Interest vs Path A" value={'-' + formatCurrency(result.extraInterestCost)} negative />
              </div>

              {/* Investment */}
              <p className="text-xs font-semibold text-muted uppercase tracking-wide mb-2">Investment (entire term)</p>
              <div className="space-y-2 mb-4">
                <ResultRow label="Investing For" value={formatMonths(result.fullTermMonths)} />
                <ResultRow label="Monthly Investment" value={formatCurrencyExact(extraMonthly)} />
                <ResultRow label="Total Contributed" value={formatCurrency(result.b_totalContributed)} />
                <ResultRow label="Investment Growth" value={formatCurrency(result.b_totalInvestGrowth)} positive />
                <ResultRow label="Investment Balance" value={formatCurrency(result.b_finalInvestment)} />
              </div>

              {/* The real bottom line */}
              <div className="border-t border-subtle pt-3 space-y-2.5">
                <ResultRow label="Investment Balance" value={formatCurrency(result.b_finalInvestment)} />
                {result.b_remainingLoan > 0 && (
                  <ResultRow label="Remaining Loan" value={'-' + formatCurrency(result.b_remainingLoan)} negative />
                )}
                <ResultRow label="Extra Interest Cost vs Path A" value={'-' + formatCurrency(result.extraInterestCost)} negative />
                <div className="border-t border-subtle pt-2.5">
                  <ResultRow label={`Adjusted Net Worth`} value={formatCurrency(result.b_adjustedNetWorth)} highlight color={PATH_B_COLOR} />
                  <p className="text-xs text-faint mt-1">
                    {formatCurrency(result.b_finalInvestment)}
                    {result.b_remainingLoan > 0 ? ` - ${formatCurrency(result.b_remainingLoan)} loan` : ''}
                    {' '}- {formatCurrency(result.extraInterestCost)} extra interest
                  </p>
                </div>
              </div>
            </GlassCard>
          </div>

          {/* Money Breakdown */}
          <GlassCard>
            <h3 className="text-sm font-semibold text-body mb-4">The Money Breakdown</h3>
            <div className="space-y-4">
              <CompareBar
                labelA="Path A: Interest Saved"
                valueA={result.interestSaved}
                labelB="Path B: Extra Interest Paid"
                valueB={result.b_totalInterest - result.a_totalInterest}
                colorA={PATH_A_COLOR}
                colorB="#f87171"
                noteA={`You save ${formatCurrency(result.interestSaved)} by paying off in ${formatMonths(result.a_payoffMonth)} instead of ${formatMonths(result.baselinePayoffMonth)}`}
                noteB={`Path B pays ${formatCurrency(result.b_totalInterest)} interest over the full ${formatMonths(result.fullTermMonths)}`}
              />

              <CompareBar
                labelA="Path A: Investment Balance"
                valueA={result.a_finalInvestment}
                labelB="Path B: Investment Balance"
                valueB={result.b_finalInvestment}
                colorA={PATH_A_COLOR}
                colorB={PATH_B_COLOR}
                noteA={`${formatCurrency(result.a_totalContributed)} contributed over ${formatMonths(result.fullTermMonths - result.a_payoffMonth)} + ${formatCurrency(result.a_totalInvestGrowth)} growth`}
                noteB={`${formatCurrency(result.b_totalContributed)} contributed over ${formatMonths(result.fullTermMonths)} + ${formatCurrency(result.b_totalInvestGrowth)} growth`}
              />

              <CompareBar
                labelA="Path B: Extra Interest Cost"
                valueA={result.extraInterestCost}
                labelB=""
                valueB={0}
                colorA="#f87171"
                colorB="transparent"
                noteA={`Path B pays ${formatCurrency(result.b_totalInterest)} total interest vs Path A's ${formatCurrency(result.a_totalInterest)} = ${formatCurrency(result.extraInterestCost)} more`}
                hideB
              />

              {/* Bottom line */}
              <div className="border-t border-subtle pt-3">
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div className="p-3 rounded-xl" style={{ backgroundColor: PATH_A_COLOR + '11', border: `1px solid ${PATH_A_COLOR}33` }}>
                    <p className="text-xs text-muted mb-1">Path A Net Worth</p>
                    <p className="text-lg font-bold" style={{ color: PATH_A_COLOR }}>{formatCurrency(result.a_netWorth)}</p>
                    <p className="text-xs text-faint mt-0.5">$0 debt + {formatCurrency(result.a_finalInvestment)} invested</p>
                  </div>
                  <div className="p-3 rounded-xl" style={{ backgroundColor: PATH_B_COLOR + '11', border: `1px solid ${PATH_B_COLOR}33` }}>
                    <p className="text-xs text-muted mb-1">Path B Adjusted Net Worth</p>
                    <p className="text-lg font-bold" style={{ color: PATH_B_COLOR }}>{formatCurrency(result.b_adjustedNetWorth)}</p>
                    <p className="text-xs text-faint mt-0.5">
                      {formatCurrency(result.b_finalInvestment)} invested
                      {result.b_remainingLoan > 0 ? ` - ${formatCurrency(result.b_remainingLoan)} owed` : ''}
                      {' '}- {formatCurrency(result.extraInterestCost)} extra interest
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-heading">
                    {result.winner === 'payoff' ? 'Path A wins by' : 'Path B wins by'}
                  </p>
                  <p className="text-xl font-bold" style={{ color: result.winner === 'payoff' ? PATH_A_COLOR : PATH_B_COLOR }}>
                    +{formatCurrency(result.advantage)}
                  </p>
                </div>
              </div>
            </div>
          </GlassCard>

          {/* Net Worth Chart */}
          {netWorthChartData.length > 1 && (
            <GlassCard>
              <h3 className="text-sm font-semibold text-body mb-1">Net Worth Over Time</h3>
              <p className="text-xs text-muted mb-4">Both paths compared over the full {formatMonths(result.fullTermMonths)} loan term</p>
              <div ref={chartRef} className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={netWorthChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={colors.chartGrid} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fill: colors.chartTick }}
                      interval={Math.floor(netWorthChartData.length / 6)}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: colors.chartTick }}
                      tickFormatter={v => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'var(--card-bg)',
                        border: '1px solid var(--card-border)',
                        borderRadius: '12px',
                        backdropFilter: 'blur(12px)',
                        color: 'var(--heading)',
                      }}
                      formatter={(v) => formatCurrencyExact(v)}
                    />
                    <Legend />
                    {result.a_payoffMonth > 0 && result.a_payoffMonth < result.fullTermMonths && (
                      <ReferenceLine
                        x={netWorthChartData.find(d => d.month >= result.a_payoffMonth)?.label}
                        stroke={PATH_A_COLOR}
                        strokeDasharray="5 5"
                        label={{ value: 'Loan paid off (A)', position: 'top', fontSize: 10, fill: PATH_A_COLOR }}
                      />
                    )}
                    <Area type="monotone" dataKey="Path A: Pay Off + Invest" stroke={PATH_A_COLOR} fill={PATH_A_COLOR} fillOpacity={0.12} strokeWidth={2.5} />
                    <Area type="monotone" dataKey="Path B: Invest from Day 1" stroke={PATH_B_COLOR} fill={PATH_B_COLOR} fillOpacity={0.12} strokeWidth={2.5} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </GlassCard>
          )}

          {/* Balance Breakdown Chart */}
          {balanceChartData.length > 1 && (
            <GlassCard>
              <h3 className="text-sm font-semibold text-body mb-1">Balance Breakdown</h3>
              <p className="text-xs text-muted mb-4">Loan balances declining while investment balances grow</p>
              <div ref={balanceChartRef} className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={balanceChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={colors.chartGrid} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fill: colors.chartTick }}
                      interval={Math.floor(balanceChartData.length / 6)}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: colors.chartTick }}
                      tickFormatter={v => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'var(--card-bg)',
                        border: '1px solid var(--card-border)',
                        borderRadius: '12px',
                        backdropFilter: 'blur(12px)',
                        color: 'var(--heading)',
                      }}
                      formatter={(v) => formatCurrencyExact(v)}
                    />
                    <Legend />
                    <Area type="monotone" dataKey="A: Investment" stroke={PATH_A_COLOR} fill={PATH_A_COLOR} fillOpacity={0.1} strokeWidth={2} />
                    <Area type="monotone" dataKey="A: Loan" stroke={PATH_A_COLOR} fill="none" strokeWidth={1.5} strokeDasharray="6 3" />
                    <Area type="monotone" dataKey="B: Investment" stroke={PATH_B_COLOR} fill={PATH_B_COLOR} fillOpacity={0.1} strokeWidth={2} />
                    <Area type="monotone" dataKey="B: Loan" stroke="#f87171" fill="none" strokeWidth={1.5} strokeDasharray="6 3" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </GlassCard>
          )}

          {/* How It Works */}
          <GlassCard>
            <h3 className="text-sm font-semibold text-body mb-3">How It Works</h3>
            <div className="space-y-2 text-xs text-muted">
              <p>
                We compare both paths over the <strong className="text-body">full {formatMonths(result.fullTermMonths)} loan term</strong>.
                Without extra payments, this loan costs {formatCurrency(result.baselineTotalInterest)} in interest.
              </p>
              <p>
                <strong className="text-body">Path A (Pay Off + Invest):</strong> You pay {formatCurrencyExact(minimumPayment + extraMonthly)}/mo
                and the loan is gone in <strong className="text-body">{formatMonths(result.a_payoffMonth)}</strong>, saving {formatCurrency(result.interestSaved)} in interest.
                Then you invest the full {formatCurrencyExact(minimumPayment + extraMonthly)}/mo at {investReturnRate}% for
                the remaining <strong className="text-body">{formatMonths(result.fullTermMonths - result.a_payoffMonth)}</strong>.
                You contribute {formatCurrency(result.a_totalContributed)} which grows to <strong className="text-body">{formatCurrency(result.a_finalInvestment)}</strong>.
              </p>
              <p>
                <strong className="text-body">Path B (Invest from Day 1):</strong> You make {formatCurrencyExact(minimumPayment)}/mo minimum payments
                and invest {formatCurrencyExact(extraMonthly)}/mo at {investReturnRate}% for the full {formatMonths(result.fullTermMonths)}.
                Your investments grow to <strong className="text-body">{formatCurrency(result.b_finalInvestment)}</strong>.
                But you pay {formatCurrency(result.b_totalInterest)} in total loan interest — that's{' '}
                <strong className="text-body">{formatCurrency(result.extraInterestCost)} more</strong> than Path A.
                Adjusted net worth: {formatCurrency(result.b_finalInvestment)} - {formatCurrency(result.extraInterestCost)} = <strong className="text-body">{formatCurrency(result.b_adjustedNetWorth)}</strong>.
              </p>
              <p className="border-t border-subtle pt-2">
                <strong className="text-body">Key tradeoff:</strong> Path A invests more per month ({formatCurrencyExact(minimumPayment + extraMonthly)} vs {formatCurrencyExact(extraMonthly)})
                but starts later. Path B invests less per month but compounds for longer — however,
                the extra interest cost of not paying off the loan eats into those gains.
                This assumes consistent {investReturnRate}% returns — real markets fluctuate.
              </p>
            </div>
          </GlassCard>
        </>
      )}
    </div>
  )
}

function formatMonths(m) {
  if (m <= 0) return '0m'
  const y = Math.floor(m / 12)
  const mo = m % 12
  return y > 0 ? (mo > 0 ? `${y}y ${mo}m` : `${y}y`) : `${mo}m`
}

function InputField({ label, value, onChange, prefix, suffix, step, min }) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted mb-1.5">{label}</label>
      <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-faint">{prefix}</span>}
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          step={step}
          min={min}
          className={`w-full bg-input border border-input rounded-xl py-2.5 text-sm text-heading focus:outline-none focus:ring-1 ${
            prefix && suffix ? 'pl-7 pr-16' : prefix ? 'pl-7 pr-3' : suffix ? 'pl-3 pr-16' : 'px-3'
          }`}
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-faint">{suffix}</span>}
      </div>
    </div>
  )
}

function MiniStat({ label, value }) {
  return (
    <div className="p-3 rounded-xl bg-subtle">
      <p className="text-xs text-muted mb-0.5">{label}</p>
      <p className="text-sm font-semibold text-heading">{value}</p>
    </div>
  )
}

function ResultRow({ label, value, highlight, positive, negative, color }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`text-sm ${highlight ? 'font-semibold text-heading' : 'text-muted'}`}>{label}</span>
      <span
        className={`text-sm font-medium ${positive ? 'text-positive' : negative ? 'text-negative' : highlight ? 'text-lg font-bold' : 'text-heading'}`}
        style={highlight && color ? { color } : {}}
      >
        {value}
      </span>
    </div>
  )
}

function CompareBar({ labelA, valueA, labelB, valueB, colorA, colorB, noteA, noteB, hideB }) {
  const maxVal = Math.max(valueA, hideB ? valueA : valueB, 1)
  return (
    <div className="space-y-2">
      <div>
        <p className="text-xs font-semibold text-muted uppercase tracking-wide mb-1.5">{labelA}</p>
        <div className="flex-1 h-9 rounded-xl bg-subtle relative overflow-hidden">
          <div
            className="h-full rounded-xl flex items-center px-3"
            style={{ width: `${Math.min(100, Math.max(8, (valueA / maxVal) * 100))}%`, backgroundColor: colorA, opacity: 0.85 }}
          >
            <span className="text-xs font-semibold text-white whitespace-nowrap" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
              {formatCurrency(valueA)}
            </span>
          </div>
        </div>
        {noteA && <p className="text-xs text-faint mt-0.5">{noteA}</p>}
      </div>
      {!hideB && (
        <div>
          <p className="text-xs font-semibold text-muted uppercase tracking-wide mb-1.5">{labelB}</p>
          <div className="flex-1 h-9 rounded-xl bg-subtle relative overflow-hidden">
            <div
              className="h-full rounded-xl flex items-center px-3"
              style={{ width: `${Math.min(100, Math.max(8, (valueB / maxVal) * 100))}%`, backgroundColor: colorB, opacity: 0.85 }}
            >
              <span className="text-xs font-semibold text-white whitespace-nowrap" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                {formatCurrency(valueB)}
              </span>
            </div>
          </div>
          {noteB && <p className="text-xs text-faint mt-0.5">{noteB}</p>}
        </div>
      )}
    </div>
  )
}
